import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Mail, Phone, MapPin, Calendar as CalendarIcon, Send, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import { toast, Toaster } from 'sonner';
import { useLanguage } from '../context/LanguageContext';

const ContactPage = () => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const { t } = useLanguage();
  const location = useLocation();

  // Handle hash scroll on page load
  useEffect(() => {
    if (location.hash) {
      const elementId = location.hash.replace('#', '');
      // Small delay to ensure page is rendered
      setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location.hash]);

  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    date: null,
    notes: ''
  });

  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm)
      });
      
      if (!response.ok) {
        throw new Error('Failed to send message');
      }
      
      toast.success('Message sent successfully!', {
        description: 'We\'ll get back to you within 24 hours.'
      });
      setContactForm({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message', {
        description: 'Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const bookingData = {
        ...bookingForm,
        date: bookingForm.date ? format(bookingForm.date, 'yyyy-MM-dd') : null
      };
      
      const response = await fetch(`${API_URL}/api/booking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit booking');
      }
      
      toast.success('Booking request received!', {
        description: 'We\'ll confirm your appointment shortly.'
      });
      setBookingForm({ name: '', email: '', phone: '', service: '', date: null, notes: '' });
    } catch (error) {
      toast.error('Failed to submit booking', {
        description: 'Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!subscribeEmail) return;
    
    try {
      const response = await fetch(`${API_URL}/api/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: subscribeEmail })
      });
      
      if (response.status === 400) {
        toast.info('Already subscribed!', {
          description: 'This email is already on our mailing list.'
        });
      } else if (!response.ok) {
        throw new Error('Failed to subscribe');
      } else {
        toast.success('Subscribed successfully!', {
          description: 'You\'ll receive our latest updates and offers.'
        });
      }
      setSubscribeEmail('');
    } catch (error) {
      toast.error('Failed to subscribe', {
        description: 'Please try again later.'
      });
    }
  };

  return (
    <div className="min-h-screen">
      <Toaster position="top-right" richColors />
      <Header />
      
      {/* Contact Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="font-montserrat-bold text-3xl md:text-4xl text-[#0b004c] mb-4">
              {t('getInTouch')}
            </h1>
            <p className="font-montserrat text-[#6a6a6a] max-w-2xl mx-auto">
              {t('contactSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <h2 className="font-montserrat-bold text-2xl text-[#0b004c] mb-6">
                {t('sendUsMessage')}
              </h2>
              <form onSubmit={handleContactSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact-name" className="font-montserrat">{t('name')}</Label>
                    <Input
                      id="contact-name"
                      data-testid="contact-name-input"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                      placeholder={t('name')}
                      required
                      className="font-montserrat"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-email" className="font-montserrat">{t('email')}</Label>
                    <Input
                      id="contact-email"
                      data-testid="contact-email-input"
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                      placeholder={t('email')}
                      required
                      className="font-montserrat"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-subject" className="font-montserrat">{t('subject')}</Label>
                  <Input
                    id="contact-subject"
                    data-testid="contact-subject-input"
                    value={contactForm.subject}
                    onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                    placeholder={t('subject')}
                    required
                    className="font-montserrat"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-message" className="font-montserrat">{t('message')}</Label>
                  <Textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    placeholder={t('message')}
                    rows={5}
                    required
                    className="font-montserrat"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={isSubmitting}
                  className="w-full bg-[#0b004c] hover:bg-[#6a6a6a] text-white font-montserrat-bold"
                >
                  {isSubmitting ? t('sending') : t('sendMessage')}
                  <Send className="w-4 h-4 ml-2" />
                </Button>
              </form>
            </div>

            {/* Booking Form */}
            <div id="booking-form" className="bg-[#0b004c] rounded-lg p-8">
              <h2 className="font-montserrat-bold text-2xl text-white mb-6">
                {t('bookConsultationTitle')}
              </h2>
              <form onSubmit={handleBookingSubmit} className="space-y-6" data-testid="booking-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="booking-name" className="font-montserrat text-white">{t('name')}</Label>
                    <Input
                      id="booking-name"
                      data-testid="booking-name-input"
                      value={bookingForm.name}
                      onChange={(e) => setBookingForm({...bookingForm, name: e.target.value})}
                      placeholder={t('name')}
                      required
                      className="font-montserrat bg-white/10 border-white/20 text-white placeholder:text-white/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="booking-email" className="font-montserrat text-white">{t('email')}</Label>
                    <Input
                      id="booking-email"
                      data-testid="booking-email-input"
                      type="email"
                      value={bookingForm.email}
                      onChange={(e) => setBookingForm({...bookingForm, email: e.target.value})}
                      placeholder={t('email')}
                      required
                      className="font-montserrat bg-white/10 border-white/20 text-white placeholder:text-white/50"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="booking-phone" className="font-montserrat text-white">{t('phone')}</Label>
                    <Input
                      id="booking-phone"
                      data-testid="booking-phone-input"
                      value={bookingForm.phone}
                      onChange={(e) => setBookingForm({...bookingForm, phone: e.target.value})}
                      placeholder="+597 xxx xxxx"
                      className="font-montserrat bg-white/10 border-white/20 text-white placeholder:text-white/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="booking-service" className="font-montserrat text-white">{t('service')}</Label>
                    <select
                      id="booking-service"
                      data-testid="booking-service-select"
                      value={bookingForm.service}
                      onChange={(e) => setBookingForm({...bookingForm, service: e.target.value})}
                      required
                      className="w-full h-10 px-3 rounded-md font-montserrat bg-white/10 border border-white/20 text-white"
                    >
                      <option value="" className="text-gray-900">{t('selectService')}</option>
                      <option value="video" className="text-gray-900">{t('videoService')}</option>
                      <option value="post" className="text-gray-900">{t('postService')}</option>
                      <option value="marketing" className="text-gray-900">{t('marketingService')}</option>
                      <option value="web" className="text-gray-900">{t('webService')}</option>
                    </select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="font-montserrat text-white">{t('preferredDate')}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        data-testid="booking-date-picker"
                        className="w-full justify-start text-left font-montserrat bg-white/10 border-white/20 text-white hover:bg-white/20"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {bookingForm.date ? format(bookingForm.date, 'PPP') : t('pickDate')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={bookingForm.date}
                        onSelect={(date) => setBookingForm({...bookingForm, date})}
                        initialFocus
                        disabled={(date) => date < new Date()}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="booking-notes" className="font-montserrat text-white">{t('additionalNotes')}</Label>
                  <Textarea
                    id="booking-notes"
                    data-testid="booking-notes-input"
                    value={bookingForm.notes}
                    onChange={(e) => setBookingForm({...bookingForm, notes: e.target.value})}
                    placeholder={t('additionalNotes')}
                    rows={3}
                    className="font-montserrat bg-white/10 border-white/20 text-white placeholder:text-white/50"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="booking-submit-btn"
                  disabled={isSubmitting}
                  className="w-full bg-white text-[#0b004c] hover:bg-[#6a6a6a] hover:text-white font-montserrat-bold"
                >
                  {isSubmitting ? t('booking') : t('bookConsultation')}
                  <CalendarIcon className="w-4 h-4 ml-2" />
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info & Map */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="font-montserrat-bold text-2xl text-[#0b004c] mb-8">
                {t('contactInfo')}
              </h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#0b004c] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-montserrat-bold text-lg text-[#0b004c]">{t('email')}</h3>
                    <a href="mailto:brian@datasignz.nl" className="font-montserrat text-[#6a6a6a] hover:text-[#0b004c] transition-colors">
                      brian@datasignz.nl
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#0b004c] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-montserrat-bold text-lg text-[#0b004c]">{t('phone')}</h3>
                    <a href="https://wa.me/5977494806" className="font-montserrat text-[#6a6a6a] hover:text-[#0b004c] transition-colors">+597 749 4806</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#0b004c] rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-montserrat-bold text-lg text-[#0b004c]">{t('visitUs')}</h3>
                    <p className="font-montserrat text-[#6a6a6a]">Daniel Coutinhostraat 14<br/>Paramaribo, Suriname</p>
                  </div>
                </div>
              </div>

              {/* Subscribe Section */}
              <div className="mt-12 p-6 bg-white rounded-lg border border-gray-200">
                <h3 className="font-montserrat-bold text-lg text-[#0b004c] mb-2">
                  {t('subscribeNewsletter')}
                </h3>
                <p className="font-montserrat text-sm text-[#6a6a6a] mb-4">
                  {t('newsletterDesc')}
                </p>
                <form onSubmit={handleSubscribe} className="flex gap-2" data-testid="newsletter-form">
                  <Input
                    type="email"
                    data-testid="newsletter-email-input"
                    value={subscribeEmail}
                    onChange={(e) => setSubscribeEmail(e.target.value)}
                    placeholder={t('email')}
                    required
                    className="font-montserrat flex-1"
                  />
                  <Button
                    type="submit"
                    data-testid="newsletter-submit-btn"
                    className="bg-[#0b004c] hover:bg-[#6a6a6a] text-white font-montserrat-bold"
                  >
                    <CheckCircle className="w-4 h-4" />
                  </Button>
                </form>
              </div>
            </div>

            {/* Google Map */}
            <div className="h-[400px] lg:h-full min-h-[400px] rounded-lg overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d3961.234567890!2d-55.17733773251842!3d5.822880057674189!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2snl!4v1234567890&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Datasignz CreativeMinds Location"
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
